import React, { useState, useContext, useRef, useEffect } from "react";
import { Link, withRouter } from "react-router-dom";
import DataTable from "core/ui/data-table";
import FileUploader from "core/ui/forms/file-uploader";
import SessionContext from "SessionContext";
import FormatDateTime from "core/utils/FormatDateTime";
import FormatNumber from "../core/utils/FormatNumber";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import ConfirmSuccess from "../core/ui/confirm-success";
import AuthTokenContext from "AuthTokenContext";
import { handleDownloadSource } from "../core/utils/download-source-file.js"


function UploadActions(props) {
  const session = useContext(SessionContext);
  const tokens = useContext(AuthTokenContext);
  const upload = props;
  const access = props.access;
  const deleteUpload = props.deleteUpload;
  const destination = (
    props.status === "Uploaded" || props.status === "Mapping"
      ? "/process-upload/"
      : "/upload-details/"
  ) + upload.id;

  let action = "";


  if ((access('upload_edit') || access('upload_create')) && props.status !== "Complete") {
    action = (
      <li>
        <Button color="link" className="upload-link-btn">
          <Link to={{ pathname: destination, state: { refer: "uploads" } }}>
            <i className="bi bi-ui-checks" /> Map Fields & Submit
          </Link>
        </Button>
        {access('upload_delete') && (
          <>
            <br />
            <Button color="link" size="sm" onClick={() => deleteUpload(upload)}>
              <i className="bi bi-trash" /> Delete
            </Button>
          </>
        )}
        {access('administration') && (
          <>
            <br />
            <Button color="link" size="sm" onClick={() => handleDownloadSource(upload, tokens, session)}>
              <i className="bi bi-download" /> Download Source
            </Button>
          </>
        )}
      </li>
    );
  } else if (access('upload_view') && props.status === "Complete") {
    action = (
      <li>
        <Button color="link" className="upload-link-btn">
          <Link to={{ pathname: destination, state: { refer: "uploads" } }}>
            <i className="bi bi-eye" /> Details
          </Link>
        </Button>
        {access('upload_delete') && (
          <>
            <Button color="link" size="sm" onClick={() => deleteUpload(upload)}>
              <i className="bi bi-trash" /> Delete
            </Button>
          </>
        )}
        {access('administration') && (
          <>
            <br />
            <Button color="link" size="sm" onClick={() => handleDownloadSource(upload, tokens, session)}>
              <i className="bi bi-download" /> Download Source
            </Button>
          </>
        )}
      </li>
    );
  }

  return (
    <ul className="action-menu">
      {action}
    </ul>
  );
}

function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const Uploads = (props) => {
  const context = useContext(SessionContext);
  const abortFlagRef = useRef(false);
  const fileUploadCompleteRef = useRef(false);
  const deleteTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(deleteTimerRef.current);
    };
  }, []);
  const [upload, setUpload] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [tableKey, setTableKey] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const table_columns = [
    { header: "Filename", key: "filename" },
    { 
      header: "Description", 
      key: "description",
      render: (value) => <span className="truncate-description-text" title={value.description}>{value.description}</span>,
    },
    {
      header: "Uploaded",
      key: "ingress_date",
      render: (value) => <FormatDateTime date={value.ingress_date} />,
    },
    { header: "Uploaded By", key: "uploaded_by" },
    {
      header: "Input Total",
      key: "input_count",
      render: (value) => <FormatNumber value={value.input_count} />,
    },
    {
      header: "Match Total",
      key: "match_count",
      render: (value) => <FormatNumber value={value.match_count} />,
    },
    {
      header: "Status",
      key: "status",
      render: (value) => onClickUploadStatus(value),
    },
  ];

  function onClickUploadStatus(value) {
    const orderId = { id: value.id };
    const pathBack = { back: window.location.pathname };
    const info = {...orderId, ...pathBack}
    return (
      <div className="status-link">
        <i className="fa fa-circle-info i-info" />{" "}
        <Link to={{ pathname: "/upload-status", state: info }}>
          {value.status}
        </Link>
      </div>
    );
  }

  function onFileUploadStart(file) {
    const reader = new FileReader();
    reader.onload = async () => {
      let upload;
      let failed = false;
      while (!fileUploadCompleteRef.current) {
        await timeout(500);
      }
      if (!abortFlagRef.current) {
        upload = await context
          .query("uploads/get", { id: file.id })
          .catch((e) => {
            console.log(e);
            failed = true;
          });
        if (!failed) {
          setUpload(upload);
          if (
            upload.content_descriptor &&
            upload.content_descriptor.columns
          ) {
            props.history.push(`/process-upload/${file.id}`);
          }
        }
      } else {
        failed = true;
        //reset flag for next upload
        abortFlagRef.current = false;
      }
    };
    reader.readAsArrayBuffer(file.slice(0, 4096 * 4));
  }

  function onAskDeleteUpload(_file) {
    setFileToDelete(_file);
    toggleDeleteModal();
  }

  function toggleDeleteModal() {
    setConfirmDelete((v) => !v);
  }

  function onCancelDelete() {
    setFileToDelete(null);
    toggleDeleteModal();
  }

  function onFinalDeleteUpload() {
    if (!fileToDelete) return;
    context.command(`upload/delete?id=${fileToDelete.id}`)
      .then(() => {
        toggleDeleteModal();
        setShowSuccess(true);
        setFileToDelete(null);
        deleteTimerRef.current = setTimeout(() => {
          setShowSuccess(false);
          setTableKey((v) => !v);
        }, 2000);
      })
      .catch((err) => {
        console.log("Delete Failed", JSON.stringify(err));
      })
  }

  const access = context.appState.userCan;
  return (
    <div className="page uploads-page">
      <div className="list-header sticky-page-header">
        <h1>Uploads</h1>
      </div>
      {access('upload_create') &&
      <FileUploader
        accept=".csv, .xlsx, .txt"
        multiple={false}
        onFileUploadStart={onFileUploadStart}
        abortFlag={(value) => { abortFlagRef.current = value; }}
        onFileUploadComplete={(value) => { fileUploadCompleteRef.current = value; }}
        isLocationUpload={false}
      />
      } 
      <div className="past-uploads" key={tableKey}>
        <DataTable
          query="uploads/list"
          cols={table_columns}
          actions={{
            component: UploadActions,
            access: access,
            upload: upload,
            deleteUpload: onAskDeleteUpload
          }}
          empty="No Uploads Found"
        />
      </div>
      <Modal centered backdrop
        isOpen={confirmDelete}
      >
        <ModalHeader>
          Delete Upload
        </ModalHeader>
        <ModalBody>
          <Alert color="light" className="d-flex">
            <i className="bi bi-exclamation-octagon"></i>
            <div>
            Selecting <b>delete</b> will permanently remove all instances from AudienceMaker and cannot be undone. Are you sure you want to continue?
            </div>
          </Alert>
          <Alert color="light" className="">
            <p>
              <span className="header-modal-text">Upload File Name</span>
              <span className="d-block ms-3">{fileToDelete && fileToDelete.filename}</span>
            </p>
            <p>
              <span className="header-modal-text">Description</span>
              <span className="d-block ms-3">{fileToDelete && fileToDelete.description}</span>
            </p>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button className="promote" onClick={onCancelDelete}>Cancel</Button>
          <Button onClick={onFinalDeleteUpload}>Delete</Button>
        </ModalFooter>
      </Modal>
      <ConfirmSuccess show={showSuccess} label="Upload deleted successfully." />
    </div>
  );
};
export default withRouter(Uploads);
